import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useEffect, useRef, useState } from "react";
import { ALPHA, INK, MOTION, NEUTRAL, SURFACE } from "../../../theme/tokens";

type Props = {
	value: string;
	/** Fired once, with the trimmed text, when the edit is committed (Enter or blur). */
	onCommit: (next: string) => void;
	/** Accessible name — the control has no visible label. */
	ariaLabel: string;
	placeholder?: string;
	maxLength?: number;
	disabled?: boolean;
	/**
	 * Every change to the text being typed — each keystroke, and the revert on Escape or on an empty or
	 * unchanged commit — so the caller can check the text while it's typed rather than only on commit.
	 */
	onDraftChange?: (draft: string) => void;
	/**
	 * The caller found the typed text unusable: draws the edge in the error tone and sets `aria-invalid`.
	 * The caller shows the reason (and points `ariaDescribedBy` at it) and decides what a commit does.
	 */
	invalid?: boolean;
	/** Id of the element that explains the field — typically the caller's error message. */
	ariaDescribedBy?: string;
	/**
	 * Escape puts the saved text back WITHOUT committing. Without it, the blur that Escape causes commits
	 * the text that was typed before the revert lands. Opt-in rather than fixed for everyone only so
	 * existing callers keep their exact behavior until each is checked; new callers should turn it on.
	 */
	revertOnEscape?: boolean;
	/**
	 * Typography/width of the rendered text, so this control can BE a row's title rather than look like
	 * a field parked next to one.
	 */
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/** Longest a name is allowed to get by default — beyond this it stops being a label. */
const DEFAULT_MAX_LENGTH = 60;

/**
 * Text that IS the label until you touch it: rendered borderless, it grows a field's affordance
 * (background + hairline) on hover and focus, commits on Enter or blur, and reverts on Escape.
 *
 * For renaming something in place — a row's title, a saved view, a suggested hire — where a labelled
 * `SystemValidationTextField` would announce "form" on a surface that is meant to read as content.
 * An empty submission reverts instead of committing: a blank name is never what was meant.
 *
 * Always a real `<input>` (never a text→input swap) so focus, caret position, and IME composition are
 * the browser's job and a click lands in the text the user aimed at.
 */
export const InlineEditableText: FC<Props> = ({
	value,
	onCommit,
	ariaLabel,
	placeholder,
	maxLength = DEFAULT_MAX_LENGTH,
	disabled,
	onDraftChange,
	// DEFAULT: an optional, additive prop — existing callers never validate, so their text is never invalid.
	invalid = false,
	ariaDescribedBy,
	// DEFAULT: an optional, additive prop — off keeps every existing caller exactly as it was.
	revertOnEscape = false,
	sx,
	dataTestId,
}) => {
	const [draft, setDraft] = useState(value);
	const inputRef = useRef<HTMLInputElement>(null);
	/** Set for the duration of Escape's own blur, which then must not commit. */
	const escapingRef = useRef<boolean>(false);

	// Follow the source of truth when it changes underneath us (a reset, a refetch, a sibling edit).
	useEffect(() => setDraft(value), [value]);

	const updateDraft = (next: string) => {
		setDraft(next);
		onDraftChange?.(next);
	};

	const commit = () => {
		if (escapingRef.current) return;
		const next = draft.trim();
		if (next === "" || next === value) {
			updateDraft(value);
			return;
		}
		onCommit(next);
	};

	return (
		<Box
			component="input"
			ref={inputRef}
			type="text"
			value={draft}
			disabled={disabled}
			aria-label={ariaLabel}
			aria-invalid={invalid || undefined}
			aria-describedby={ariaDescribedBy}
			placeholder={placeholder}
			maxLength={maxLength}
			data-testid={dataTestId}
			onChange={(event: React.ChangeEvent<HTMLInputElement>) => updateDraft(event.target.value)}
			onBlur={commit}
			onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
				if (event.key === "Enter") {
					event.preventDefault();
					commit();
					inputRef.current?.blur();
					return;
				}
				if (event.key === "Escape") {
					event.preventDefault();
					updateDraft(value);
					// `blur()` runs the blur handler synchronously, so the flag covers exactly that one commit.
					escapingRef.current = revertOnEscape;
					inputRef.current?.blur();
					escapingRef.current = false;
				}
			}}
			sx={[
				(theme) => ({
					minWidth: 0,
					px: 0.75,
					py: 0.25,
					border: "1px solid transparent",
					borderRadius: 1,
					backgroundColor: "transparent",
					color: INK.PRIMARY,
					font: "inherit",
					textOverflow: "ellipsis",
					cursor: disabled ? "default" : "text",
					transition: theme.transitions.create(["background-color", "border-color"], {
						duration: MOTION.DURATION.HOVER,
						easing: MOTION.EASE_OUT,
					}),
					"&::placeholder": { color: INK.DISABLED, opacity: 1 },
					"@media (hover: hover) and (pointer: fine)": {
						"&:hover:not(:disabled)": { backgroundColor: NEUTRAL[50], borderColor: NEUTRAL[200] },
					},
					// `:not(:disabled)` keeps this at the same specificity as the hover rule above so, being
					// later in source order, focus wins while an element is BOTH hovered and focused —
					// otherwise the hover border silently overrides the focus one.
					"&:focus:not(:disabled)": {
						outline: "none",
						backgroundColor: SURFACE,
						borderColor: theme.palette.primary.main,
						boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}`,
					},
					// Last, and one notch more specific, so an unusable name keeps its error edge through
					// hover and focus — the moment the user is looking at it is when it has to show.
					"&[aria-invalid='true']:not(:disabled), &[aria-invalid='true']:focus:not(:disabled)": {
						borderColor: theme.palette.error.main,
						boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, ALPHA.CONTROL_TINT_HOVER)}`,
					},
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				}),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		/>
	);
};

export default InlineEditableText;
